import {
  LegacyDashboardProvider,
  useSimInventorySelectionStore,
  type LegacyDashboardTypes,
  type SimInventoryCommonTypes,
} from '@hologram-hyper-dashboard/components';
import useDebounceCallback from 'activation/common/hooks/useDebounceCallback';
import LoadingScreen from 'activation/LoadingScreen';
import sendAnalyticsEvent from 'common-js/analytics/analytics';
import { getOrgIdFromContextData, getUserContextDataMemoized } from 'common-js/api/util';
import { ACCOUNT_BILLING } from 'common-js/constants/paths';
import { useAppDispatch, useAppSelector } from 'common-js/hooks';
import { preflightActivationPreview } from 'common-js/reducers/activation/actions/actions';
import {
  clearSelection,
  ensureDevicesPersisted,
  setDeviceSelection,
} from 'common-js/reducers/devices/actions';
import { pushGlobalMessage } from 'common-js/reducers/message/actions';
import useOnBulkUpdatePlan from 'device/Plan/useOnBulkUpdatePlan';
import useBulkModal from 'devices/ManageDropdown/useBulkModal';
import useDeactivateSIMItem from 'devices/ManageDropdown/useItems/useDeactivateSIMItem';
import useRenameItem from 'devices/ManageDropdown/useItems/useRenameItem';
import useSetDataUsageLimitsItem from 'devices/ManageDropdown/useItems/useSetDataUsageLimitsItem';
import useTransferToOrganizationItem from 'devices/ManageDropdown/useItems/useTransferToOrganizationItem';
import { useCallback, useState, type FC, type ReactNode } from 'react';

interface SimToLegacyAdapterProps {
  children?: ReactNode;
  segment?: SimInventoryCommonTypes.SimInventorySegmentName | null;
}

const simsAreAllOnSamePlan = (sims: any[] = []) => {
  if (!sims || sims.length <= 1) {
    return true;
  }
  return sims.every(({ planId }) => planId === sims[0].planId);
};

const SimToLegacyAdapter: FC<SimToLegacyAdapterProps> = ({ children, segment }) => {
  const dispatch = useAppDispatch();
  const userContextData = useAppSelector(getUserContextDataMemoized);
  const orgId = getOrgIdFromContextData(userContextData)!;
  const { resetSelection } = useSimInventorySelectionStore();

  // Shared handlers
  const onSuccessNeedsClearSelection = useCallback(() => {
    if (segment) {
      resetSelection(segment);
    }
  }, [resetSelection, segment]);

  const preDevicesAction = useCallback(
    async (deviceIds: Array<DeviceId>) => {
      dispatch(clearSelection());
      dispatch(setDeviceSelection(deviceIds));
      await dispatch(ensureDevicesPersisted(deviceIds, orgId));
    },
    [dispatch, orgId]
  );

  // Deactivate
  const [deactivated, setDeactivated] = useState<boolean>(false);

  const { onClick: onDeactivateClick } = useDeactivateSIMItem({
    deviceType: segment as any,
    totalSelected: 2,
    onSuccess: onSuccessNeedsClearSelection,
  }); // Doesn't matter what total selected is for us

  const onDeactivate = useCallback(
    async (deviceIds: Array<DeviceId>) => {
      await preDevicesAction(deviceIds);
      setDeactivated(true);
    },
    [preDevicesAction]
  );

  const triggerDeactivate = useCallback(() => {
    if (deactivated) {
      setDeactivated(false);
      onDeactivateClick();
    }
  }, [deactivated, onDeactivateClick]);

  useDebounceCallback(triggerDeactivate, 0);

  // Transfer to organization
  const [transferToOrg, setTransferToOrg] = useState<boolean>(false);

  const { onClick: onTransferToOrganizationClick } = useTransferToOrganizationItem({
    onSuccess: onSuccessNeedsClearSelection,
  });

  const onTransferToOrg = useCallback(
    async (deviceIds: Array<DeviceId>) => {
      await preDevicesAction(deviceIds);
      setTransferToOrg(true);
    },
    [preDevicesAction]
  );

  const triggerTransferToOrg = useCallback(() => {
    if (transferToOrg) {
      setTransferToOrg(false);
      onTransferToOrganizationClick();
    }
  }, [transferToOrg, onTransferToOrganizationClick]);

  useDebounceCallback(triggerTransferToOrg, 0);

  // Set data usage limits
  const [setDataUsageLimits, setSetDataUsageLimits] = useState<boolean>(false);

  const { onClick: onSetDataUsageLimitsClick } = useSetDataUsageLimitsItem();

  const onSetDataUsageLimits = useCallback(
    async (deviceIds: Array<DeviceId>) => {
      await preDevicesAction(deviceIds);
      setSetDataUsageLimits(true);
    },
    [preDevicesAction]
  );

  const triggerSetDataUsageLimits = useCallback(() => {
    if (setDataUsageLimits) {
      setSetDataUsageLimits(false);
      onSetDataUsageLimitsClick();
    }
  }, [setDataUsageLimits, onSetDataUsageLimitsClick]);

  useDebounceCallback(triggerSetDataUsageLimits, 0);

  // Rename
  const [rename, setRename] = useState<boolean>(false);

  const { onClick: onRenameClick } = useRenameItem({
    totalSelected: 2,
  });

  const onRename = useCallback(
    async (deviceIds: Array<DeviceId>) => {
      await preDevicesAction(deviceIds);
      setRename(true);
    },
    [preDevicesAction]
  );

  const triggerRename = useCallback(() => {
    if (rename) {
      setRename(false);
      onRenameClick();
    }
  }, [rename, onRenameClick]);

  useDebounceCallback(triggerRename, 0);

  // End testing and activate
  const [preparingBulkActivatePreflight, setPreparingBulkActivatePreflight] =
    useState<boolean>(false);
  const openBulkActivatePreflightModal = useBulkModal('BulkActivatePreflightModal');
  const endTestingAndActivate = useCallback<(devices: Array<DeviceId>, page?: string) => void>(
    async (devices) => {
      setPreparingBulkActivatePreflight(true);
      await dispatch(setDeviceSelection(devices));
      // await dispatch(ensureDevicesPersisted(devices, orgId));

      const preflightPreview = await dispatch(preflightActivationPreview());
      const { sims } = preflightPreview ?? {};

      if (!preflightPreview || (sims?.length ?? 0) === 0) {
        sendAnalyticsEvent({
          type: 'ANALYTICS_SYSTEM_ERROR',
          data: { error: 'No SIMs returned from preflightActivationPreview' },
        });
      }
      if (!simsAreAllOnSamePlan(sims)) {
        setPreparingBulkActivatePreflight(false);
        dispatch(
          pushGlobalMessage(
            'Choose devices on the same plan to force activate Preflight SIMs.',
            'error'
          )
        );
      } else {
        setPreparingBulkActivatePreflight(false);
        openBulkActivatePreflightModal();
      }
    },
    [dispatch, openBulkActivatePreflightModal]
  );

  // Change plan
  const [preparing, setPreparing] = useState<boolean>(false);

  const onBulkUpdatePlan = useOnBulkUpdatePlan();

  const handleBulkUpdatePlan = useCallback<LegacyDashboardTypes.OnBulkChangePlan>(
    async (deviceIds, page) => {
      setPreparing(true);

      window.setTimeout(async () => {
        try {
          await onBulkUpdatePlan(deviceIds, page);
        } finally {
          setPreparing(false);
        }
      }, 0);
    },
    [onBulkUpdatePlan]
  );

  return (
    <LegacyDashboardProvider
      legacyDashboardContext={{
        onBulkChangePlan: handleBulkUpdatePlan,
        endTestingAndActivate,
        onDeactivate,
        rename: onRename,
        setDataUsageLimits: onSetDataUsageLimits,
        transferToOrganization: onTransferToOrg,
        billingPageUrl: ACCOUNT_BILLING,
      }}
    >
      <>
        {(preparing || preparingBulkActivatePreflight) && (
          <LoadingScreen
            content={
              preparing
                ? "We're setting up the change plan process..."
                : "We're setting up the activation process..."
            }
            title={preparing ? 'Change data plan' : 'Activate SIMs'}
          />
        )}
        {children}
      </>
    </LegacyDashboardProvider>
  );
};

export default SimToLegacyAdapter;
